/**
 * HouseRulesTab — the "House Rules" tab of Guest Experience.
 *
 * Default: the handoff's clean numbered rule list over the real house-rules
 * config (apiClient.getHouseRules), surfacing the routing model as "Guidebook /
 * Display" chips per rule. "Edit rules" reveals the full HouseRulesPage editor
 * (surface routing, delivery levels, adults-emphasis, policies) inline.
 * Demo-lock is enforced by the route's DemoGuard wrapper.
 */

import { useEffect, useState } from "react"
import { Check, Pencil, Plus, ArrowLeft, ScrollText } from "lucide-react"
import { apiClient } from "@/lib/api"
import { usePropertyContext } from "@/lib/usePropertyContext"
import { cn } from "@/lib/utils"
import { HouseRulesPage } from "@/pages/HouseRulesPage"

type Obj = Record<string, unknown>
const asObj = (v: unknown): Obj => (v && typeof v === "object" ? (v as Obj) : {})
const isOn = (v: unknown) => v === true
const str = (v: unknown) => (typeof v === "string" ? v : "")
/** surface flag with default when unset (matches the editor's DEFAULT_SURFACES) */
const surfOn = (v: unknown, dflt: boolean) => (v === undefined ? dflt : isOn(v))

function formatHM(t: string): string {
  if (!t) return ""
  const [h, m] = t.split(":").map(Number)
  if (Number.isNaN(h)) return t
  const am = h < 12
  const hr = h % 12 === 0 ? 12 : h % 12
  return `${hr}:${String(m ?? 0).padStart(2, "0")} ${am ? "AM" : "PM"}`
}

const POLICY_TITLE: Record<string, Record<string, string>> = {
  parties: { not_allowed: "No parties or events", allowed: "Parties allowed", approval_required: "Parties by approval" },
  smoking: { not_allowed: "No smoking indoors", allowed: "Smoking allowed", approval_required: "Smoking by approval" },
  pets: { not_allowed: "No pets", allowed: "Pets welcome", approval_required: "Pets by approval" },
  visitors: { not_allowed: "No additional visitors", allowed: "Visitors welcome", approval_required: "Visitors by approval" },
}

interface RuleItem {
  title: string
  body: string
  surfaces?: { guidebook: boolean; display: boolean }
}

function buildItems(rules: Obj): RuleItem[] {
  const items: RuleItem[] = []

  const checkin = asObj(rules.checkin)
  if (isOn(checkin.enabled) && str(checkin.checkinTime)) {
    items.push({ title: `Check-in from ${formatHM(str(checkin.checkinTime))}`, body: "" })
  }

  const quiet = asObj(rules.quietHours)
  if (isOn(quiet.enabled)) {
    const s = asObj(quiet.surfaces)
    items.push({
      title: `Quiet hours ${formatHM(str(quiet.startTime))} – ${formatHM(str(quiet.endTime))}`,
      body: "",
      surfaces: { guidebook: surfOn(s.guidebook, true), display: surfOn(s.display, false) },
    })
  }

  for (const key of ["smoking", "pets", "parties", "visitors"] as const) {
    const rule = asObj(rules[key])
    if (!isOn(rule.enabled)) continue
    const value = str(rule.value) || "not_allowed"
    const title = POLICY_TITLE[key]?.[value] || key
    const s = asObj(rule.surfaces)
    items.push({
      title,
      body: str(rule.notes),
      surfaces: { guidebook: surfOn(s.guidebook, true), display: surfOn(s.display, false) },
    })
  }

  const checkout = asObj(rules.checkout)
  if (isOn(checkout.enabled) && str(checkout.checkoutTime)) {
    const list = Array.isArray(checkout.checklist) ? (checkout.checklist as string[]) : []
    items.push({
      title: `Checkout by ${formatHM(str(checkout.checkoutTime))}`,
      body: list.length ? list.slice(0, 2).join(" · ") : "",
    })
  }

  return items
}

export function HouseRulesTab() {
  const { propertyId } = usePropertyContext()
  const [rules, setRules] = useState<Obj>({})
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    setLoading(true)
    apiClient
      .getHouseRules(propertyId)
      .then((res) => {
        if (cancelled) return
        setRules(res.ok && res.rules ? (res.rules as Obj) : {})
      })
      .catch(() => !cancelled && setRules({}))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [propertyId])

  if (editing) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="mb-4 inline-flex items-center gap-1.5 rounded-[9px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
          Back to house rules
        </button>
        <HouseRulesPage />
      </div>
    )
  }

  const items = buildItems(rules)

  return (
    <section className="max-w-[720px] rounded-card border border-border bg-card p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-display text-[16px] font-bold tracking-[-0.01em] text-foreground">House rules</h3>
        <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-brand-strong">
          <Check className="h-3 w-3" strokeWidth={2.4} />
          Autosaved · appears on every surface
        </span>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-xl bg-line-soft" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
          <ScrollText className="h-8 w-8 text-muted-foreground" strokeWidth={1.6} />
          <p className="mt-3 text-[14px] font-semibold text-foreground">No rules set yet</p>
          <p className="mt-1 text-[12.5px] text-muted-foreground">Add house rules so guests know what to expect.</p>
        </div>
      ) : (
        <div className="flex flex-col">
          {items.map((item, i) => (
            <div key={i} className="flex items-start gap-3.5 border-t border-line-soft py-[15px] first:border-t-0">
              <span className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-brand-soft font-display text-[13px] font-bold text-brand-strong">
                {i + 1}
              </span>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-foreground" style={{ fontWeight: 650 }}>
                  {item.title}
                </div>
                {item.body && <div className="mt-0.5 text-[12.5px] leading-[1.5] text-ink-2">{item.body}</div>}
                {item.surfaces && (item.surfaces.guidebook || item.surfaces.display) && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">Shows on</span>
                    {item.surfaces.guidebook && (
                      <span className="rounded-full bg-line-soft px-2 py-0.5 text-[10.5px] font-medium text-ink-2">Guidebook</span>
                    )}
                    {item.surfaces.display && (
                      <span className="rounded-full bg-line-soft px-2 py-0.5 text-[10.5px] font-medium text-ink-2">Display</span>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-all hover:bg-line-soft hover:text-foreground"
                aria-label="Edit rule"
              >
                <Pencil className="h-3.5 w-3.5" strokeWidth={1.9} />
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => setEditing(true)}
        className="mt-4 inline-flex items-center gap-1.5 rounded-[10px] border border-dashed border-line-2 bg-card px-3.5 py-2.5 text-[12.5px] font-semibold text-ink-2 transition-colors hover:bg-line-soft"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2} />
        Add a rule
      </button>
    </section>
  )
}
